import { useMemo } from 'react';
import { ContentNode, ContentType } from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { getNextPageInTree, resolvePageForLocation } from '../../services/PageOperations';
import { useContent } from '../v2-hooks/useContent';
import { useCurrentSectionId } from '../v2-hooks/useUrlHash';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** Exposed state for the guide editor. */
export interface GuideEditorViewModel {
  /** The resolved page node whose body and sections are rendered. */
  readonly currentPage: ContentNode;
  /** The next page in depth-first order, derived from the same content snapshot. */
  readonly nextPage: ContentNode | undefined;
  /** Sections on the current page (each rendered as a SectionEditorControl). */
  readonly sections: readonly ContentNode[];
  /** Content ID for image upload and comment anchoring. */
  readonly contentId: string;
  /** Page title (string or Tiptap JSON) — heading for the page body editor. */
  readonly pageTitle: string | object;
  /**
   * True when the modify-page actions (edit title, delete page) should be shown.
   * Hidden on the root page because it must always be titled "Home".
   */
  readonly isModifyPageButtonsVisible: boolean;
}

/**
 * ViewModel hook for the guide editor.
 * Resolves the current page from the URL hash and exposes its sections.
 *
 * Returns undefined while content or the document handle is not loaded — the
 * view renders nothing during that transient state rather than crashing. Still
 * throws when the loaded content type is not Guide or Strategy.
 */
export function useGuideEditorViewModel(): GuideEditorViewModel | undefined {
  const service = useContentEditorService();
  const content = useContent();
  const currentSectionId = useCurrentSectionId();
  const loadedDocument = useLoadedDocument();

  // When content or the URL section id changes, then resolve which page to render.
  const currentPage = useMemo(
    () => (content ? resolvePageForLocation(content, currentSectionId) : undefined),
    [content, currentSectionId],
  );

  // A parent-teardown render can transiently clear content/handle before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!content || !loadedDocument) return undefined;

  // A missing page for loaded content means the editor was mounted in a state it
  // can't render. Throw intentionally to crash into the nearest error boundary
  // rather than silently rendering a broken UI.
  if (!currentPage) {
    throw new Error('GuideEditorViewModel requires a resolvable current page.');
  }

  // Same rationale as above — wrong content type means the wrong editor was mounted.
  // Crash into the error boundary instead of silently rendering a broken UI.
  const contentType = content.contentMetadata.contentType;
  if (contentType !== ContentType.Guide && contentType !== ContentType.Strategy) {
    throw new Error(`GuideEditor only supports Guide and Strategy content (got ${contentType}).`);
  }

  const contentId = loadedDocument.contentId;
  const isRootPage = currentPage.id === content.contentData.id;
  const nextPage = getNextPageInTree(content, currentPage.id);

  return {
    currentPage,
    nextPage,
    sections: currentPage.contentSections ?? [],
    contentId,
    pageTitle: currentPage.title,
    isModifyPageButtonsVisible: !isRootPage,
  };
}
